import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Stack, CircularProgress, Alert
} from '@mui/material';
import { Add, ArrowBack } from '@mui/icons-material';
import { settlementsApi, sharedExpensesApi } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';
import CreateExpenseDialog from '../CreateExpenseDialog';
import ExpenseList from './ExpenseList';
import GroupBalancesPanel from './GroupBalancesPanel';
import { formatMoney } from '../../utils/money';

const formatAmount = (value) => formatMoney(Math.abs(Number(value)));

// One group: every expense in it, balances, suggested payments and payment history
function GroupDetail({ groupId, onBack }) {
  const [ledger, setLedger] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const { showSuccess } = useNotification();

  const loadExpenses = useCallback(async () => {
    const response = await sharedExpensesApi.getAll({ filter: 'group', groupId });
    setExpenses(response.data.expenses || []);
  }, [groupId]);

  const load = useCallback(async () => {
    try {
      setError('');
      const [ledgerRes] = await Promise.all([settlementsApi.getGroup(groupId), loadExpenses()]);
      setLedger(ledgerRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load the group');
    } finally {
      setLoading(false);
    }
  }, [groupId, loadExpenses]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const addDefaults = useMemo(() => ({ splitType: 'group', groupId }), [groupId]);

  const handleCreate = async (expenseData) => {
    await sharedExpensesApi.create(expenseData);
    showSuccess('Expense created');
    await load();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  const me = ledger?.members.find(m => m.isYou);
  const total = expenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
        All friends and groups
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {ledger && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
                <Box>
                  <Typography variant="h5">{ledger.group.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {ledger.members.length} members · {expenses.length} expenses · {formatAmount(total)} total
                  </Typography>
                  {me && (
                    <Typography
                      variant="h6"
                      mt={1}
                      color={me.net > 0 ? 'success.main' : me.net < 0 ? 'error.main' : 'text.secondary'}
                    >
                      {me.net > 0
                        ? `You get back ${formatAmount(me.net)}`
                        : me.net < 0
                          ? `You owe ${formatAmount(me.net)}`
                          : 'You are settled up'}
                    </Typography>
                  )}
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={() => setAddOpen(true)}>
                  Add expense
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" mb={2}>Balances</Typography>
              <GroupBalancesPanel
                groupId={groupId}
                data={ledger}
                onData={setLedger}
                onChanged={loadExpenses}
                showPayments
              />
            </CardContent>
          </Card>
        </>
      )}

      <Typography variant="h6" mb={2}>Expenses</Typography>
      <ExpenseList
        expenses={expenses}
        showGroup={false}
        emptyText="No expenses in this group yet."
        onChanged={load}
      />

      <CreateExpenseDialog
        open={addOpen}
        defaults={addDefaults}
        onClose={() => setAddOpen(false)}
        onSuccess={handleCreate}
      />
    </Box>
  );
}

export default GroupDetail;
